import { Injectable } from '@angular/core';

/**
 * Local cache of "recently-used descriptions per job", persisted to
 * `localStorage` so it survives reloads and is shared across components.
 *
 * Why it exists: the timer-header's job picker matches typed search
 * against each job's recent descriptions so users can find a job by
 * what they were working on ("the kickoff one", "Friday review"). The
 * backend's `getRecentDescriptions` endpoint covers descriptions that
 * have been persisted long enough to show up in its query result, but
 * has subtle timing issues for descriptions that were JUST typed — a
 * description saved a moment ago might not appear in the next fetch.
 *
 * This service plugs that gap. Every code path that persists a new
 * description calls `add(jobId, desc)` immediately, so any subsequent
 * picker open / search sees the description without waiting for the
 * backend roundtrip. Multiple components (timer-header for typing,
 * timer-tracker for inline list edits + the edit modal) write through
 * here so the cache stays consistent regardless of which surface the
 * user used.
 */
@Injectable({ providedIn: 'root' })
export class RecentDescriptionsService {
    /** localStorage key. Kept under the timer-header prefix because the
     *  header was the first/main consumer; the key name is now a stable
     *  cross-component contract — don't rename without migrating. */
    private static readonly STORAGE_KEY = 'thn_session_descs';

    /** Per-job cap. Matches the timer-header's existing behaviour so
     *  the picker dropdown stays scannable. */
    private static readonly MAX_PER_JOB = 10;

    /** Add a description to the front of the per-job list. Deduped
     *  case-insensitively (an existing entry moves to the top rather
     *  than appearing twice) and capped at `MAX_PER_JOB`. Empty /
     *  whitespace-only descriptions are silently ignored.
     *
     *  Capitalises the first letter so descriptions read consistently
     *  whether the user typed `banana` or `Banana`. */
    add(jobId: number, desc: string): void {
        if (jobId == null || !desc) return;
        const trimmed = desc.trim();
        if (!trimmed) return;
        const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
        const map = this.getAll();
        const list = map.get(jobId) || [];
        const lower = capitalized.toLowerCase();
        const existingIdx = list.findIndex(d => d.toLowerCase() === lower);
        if (existingIdx !== -1) list.splice(existingIdx, 1);
        list.unshift(capitalized);
        if (list.length > RecentDescriptionsService.MAX_PER_JOB) {
            list.length = RecentDescriptionsService.MAX_PER_JOB;
        }
        map.set(jobId, list);
        this.persist(map);
    }

    /** Read the full per-job map. Returns a fresh Map every call so
     *  callers can hold it without seeing later mutations — the
     *  service is the source of truth, callers are snapshot consumers. */
    getAll(): Map<number, string[]> {
        try {
            const raw = localStorage.getItem(RecentDescriptionsService.STORAGE_KEY);
            if (!raw) return new Map();
            const entries: [number, string[]][] = JSON.parse(raw);
            return new Map(entries);
        } catch {
            // Corrupt or unavailable storage — return empty rather than
            // crashing the caller. The next successful write will repair
            // the key by overwriting it.
            return new Map();
        }
    }

    private persist(map: Map<number, string[]>): void {
        try {
            const entries = Array.from(map.entries());
            localStorage.setItem(
                RecentDescriptionsService.STORAGE_KEY,
                JSON.stringify(entries),
            );
        } catch {
            // localStorage can fail in a few edge cases (private mode
            // with a hard quota, file:// origins, etc.). Drop silently
            // — the cache is a UX nicety, not a correctness requirement.
        }
    }
}
